import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { useMedicationsSearch } from "features/medications/hooks/useMedicationsSearch";
import { useAllergyMutations } from "features/patients/hooks/useAllergyMutations";
import { onInvalidSubmit } from "utils/reportFormErrors";

import { allergyFormSchema } from "../utils/allergyFormSchema";
import { DEFAULT_VALUES } from "../utils/constants";

export const useAllergyForm = ({ isOpen, editId, data, patientId, onClose }) => {
  const [drugSearch, setDrugSearch] = useState("");
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [drugId, setDrugId] = useState(null);

  const { createOrUpdateAllergy } = useAllergyMutations(patientId);
  const { data: drugSearchResults } = useMedicationsSearch(drugSearch);

  const selectedItem = editId !== null ? data.find((item) => item.id === editId) : null;
  const isItemDisabled = !!selectedItem?.disabled;

  const form = useForm({
    resolver: zodResolver(allergyFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (isOpen) {
      if (selectedItem) {
        form.reset({
          activeTab: selectedItem.drugAllergy ? "DRUG_ALLERGY" : "ALLERGY",
          name: selectedItem.name ?? "",
          allergyReaction: selectedItem.allergyReaction ?? "RASH",
          serious: selectedItem.serious ?? false,
          drugName: selectedItem.drugName ?? "",
          drugGenericName: selectedItem.drugGenericName ?? "",
          ingredients: selectedItem.ingredients ?? "",
          note: selectedItem.note ?? null,
        });
        setDrugId(selectedItem.drugId ?? null);
        setDrugSearch("");
        setSelectedDrug(selectedItem.drugId ? { id: selectedItem.drugId } : null);
      } else {
        form.reset(DEFAULT_VALUES);
        setDrugId(null);
        setDrugSearch("");
        setSelectedDrug(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editId]);

  const handleClose = () => {
    form.reset(DEFAULT_VALUES);
    setDrugId(null);
    setDrugSearch("");
    setSelectedDrug(null);
    onClose();
  };

  const handleDrugSelect = (drug) => {
    setSelectedDrug(drug);
    setDrugId(drug.id);
    setDrugSearch(drug.tradeName);
    form.setValue("drugName", drug.tradeName, { shouldValidate: true });
    form.setValue("drugGenericName", drug.genericName);
    form.setValue("ingredients", drug.ingredients ?? "");
  };

  const handleClearDrug = () => {
    setSelectedDrug(null);
    setDrugSearch("");
    setDrugId(null);
    form.setValue("drugName", "");
    form.setValue("drugGenericName", "");
    form.setValue("ingredients", "");
  };

  const onSubmit = (values) => {
    createOrUpdateAllergy.mutate(
      {
        id: editId ?? null,
        name: values.activeTab === "DRUG_ALLERGY" ? values.drugName : values.name,
        allergyType: "ALLERGY",
        allergyReaction: values.allergyReaction,
        serious: values.serious,
        drugAllergy: values.activeTab === "DRUG_ALLERGY",
        drugId: values.activeTab === "DRUG_ALLERGY" ? drugId : null,
        drugName: values.activeTab === "DRUG_ALLERGY" ? values.drugName : null,
        drugGenericName: values.activeTab === "DRUG_ALLERGY" ? values.drugGenericName : null,
        ingredients: values.activeTab === "DRUG_ALLERGY" ? values.ingredients : null,
        ...(values.note !== null && { note: values.note }),
      },
      { onSuccess: handleClose }
    );
  };

  return {
    form,
    isItemDisabled,
    drugSearch,
    setDrugSearch,
    drugSearchResults,
    selectedDrug,
    handleDrugSelect,
    handleClearDrug,
    handleClose,
    handleSubmit: form.handleSubmit(onSubmit, onInvalidSubmit("useAllergyForm")),
    isPending: createOrUpdateAllergy.isPending,
  };
};
